/**
 * Tier-4 shells and blasts (M21, docs/TOWERS_V2.md "Tier 4"): the Rocket Mortar's salvo and Seeker Shells, the Siege
 * Battery's Earthquake and Napalm ground, the Bombard's Cluster Bomb, and the Glacier's harder freeze.
 */
import { TowerArchetype } from "../../data/balance";
import { Fix64 } from "../../maths/fix64";
import { distanceSquared } from "../../world/roadGeometry";
import { Enemy, Shell, Tower, ZoneKind } from "../entities";
import { SimEventKind } from "../events";
import type { LevelSim } from "../levelSim";
import { SimConstants } from "../constants";
import { applyHit, canTarget, findTargetExcept } from "../systems/combat";
import { launchShell } from "../systems/mortar";
import { BASE_TIERS, branchOf, skill } from "./ascension";
import { layZone } from "./statuses";

/**
 * A Mortar's shot: one shell, or the Rocket Mortar's salvo — `shells` (+ Barrage) shells at the best targets in turn (the
 * list starts over when there are fewer), each a full shell with the branch's smaller burst. Seeker Shells home on their
 * target and can hit flyers.
 */
export function launchSalvo(sim: LevelSim, t: Tower, a: TowerArchetype, target: Enemy, damage: Fix64): void {
  const b = t.tier > BASE_TIERS ? branchOf(sim, t) : null;
  if (b === null || b.id !== "rocket") { launchShell(sim, t, a, target, damage); return; }
  const count = b.tier4.int("shells", 1) + (skill(sim, t, "barrage")?.params.int("shells") ?? 0);
  const seek = (skill(sim, t, "seekerShells")?.params.int("seek") ?? 0) > 0;
  const targets: Enemy[] = [target];
  while (targets.length < count) {
    const next = findTargetExcept(sim, t, ...targets.map((e) => e.id));
    if (next === null) break;
    targets.push(next);
  }
  for (let i = 0; i < count; i++) launchShell(sim, t, a, targets[i % targets.length], damage, seek);
}

/** Seeker Shells: the rocket targets flyers as well (the Mortar archetype is ground-only). */
export function rocketHitsAir(sim: LevelSim, t: Tower): boolean { return (skill(sim, t, "seekerShells")?.params.int("seek") ?? 0) > 0; }

/** A shell landed: Earthquake leaves shaken ground, Napalm burning ground, where it burst. */
export function onShellLanded(sim: LevelSim, s: Shell, tower: Tower | null): void {
  if (tower === null || tower.tier <= BASE_TIERS) return;
  const quake = skill(sim, tower, "earthquake");
  if (quake !== null) layZone(sim, tower, ZoneKind.Earthquake, s.x, s.y, s.radius, Fix64.Zero, quake.params.num("slowFraction"), quake.params.int("zoneTicks"));
  const napalm = skill(sim, tower, "napalm");
  if (napalm !== null) {
    const dps = s.damage.mul(napalm.params.num("dpsMult"));
    layZone(sim, tower, ZoneKind.Napalm, s.x, s.y, s.radius.mul(napalm.params.num("radiusMult", Fix64.One)), dps.divInt(SimConstants.TicksPerSecond), Fix64.Zero, napalm.params.int("zoneTicks"));
  }
}

/**
 * Cluster Bomb: after a Bombard's blast at (x, y), `bomblets` bomblets strike the nearest enemies it can hit within
 * `reach` of the blast (one each, nearest first, ties → lower id) for `bombletDamageMult` of the shot.
 */
export function clusterBomb(sim: LevelSim, t: Tower, a: TowerArchetype, x: Fix64, y: Fix64, damage: Fix64): void {
  const s = skill(sim, t, "clusterBomb");
  if (s === null) return;
  const reach = s.params.num("reach"), r2 = reach.mul(reach);
  const near: Enemy[] = [];
  for (const e of sim.enemySlots) if (e.alive && canTarget(a, e) && sim.targetable(e) && distanceSquared(x, y, e.x, e.y).le(r2)) near.push(e);
  near.sort((p, q) => distanceSquared(x, y, p.x, p.y).cmp(distanceSquared(x, y, q.x, q.y)) || p.id - q.id);
  const n = Math.min(near.length, s.params.int("bomblets"));
  if (n === 0) return;
  sim.events.add(SimEventKind.SkillFired, t.id, s.index, near[0].id);
  const bomb = damage.mul(s.params.num("bombletDamageMult"));
  sim.areaDepth++;
  for (let i = 0; i < n; i++) if (near[i].alive) { sim.events.add(SimEventKind.ChainHit, near[i].id, t.id, i + 1); applyHit(sim, near[i], t, a, bomb); }
  sim.areaDepth--;
}

